/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import moment from 'moment'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import CheckboxComponent from 'components/atoms/Checkbox'
import MaskedInput from 'react-text-mask'
import { toast } from 'react-toastify'
import { useSendFaxMutation } from 'store/services/labOrder'
import { Order } from 'store/types'
import { phoneNumberMask } from 'helpers/masks'

const ModalBodyContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const DocumentsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
`

const DocumentGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const GroupHeader = styled.div`
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
`

const DocumentItem = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: ${(props) => (props.selected ? '#e7f3ff' : '#f8f9fa')};
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${(props) => (props.selected ? '#d0e7ff' : '#e9ecef')};
  }
`

const DocumentInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 0.75rem;
`

const DocumentName = styled.div`
  font-weight: 500;
  color: #212529;
`

const DocumentMeta = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.25rem;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Label = styled.label`
  font-weight: 500;
  color: #212529;
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.875rem;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #2c83bd;
    box-shadow: 0 0 0 3px rgba(44, 131, 189, 0.1);
  }
`

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`

interface IProps {
  order: Order
  open: boolean
  onClose: () => void
}

const documentSortOrder = [
  'RX',
  'RX_CANDIDATE',
  'AOB',
  'OFFICE_NOTES',
  'BILLING_DOC',
  'OTHER',
  'SUPPLEMENTAL',
  'REPORT_PDF',
  'REPORT_PDF_ANY',
  'OTHER_BRIGHTREE',
].reduce(
  (acc, type, idx) => {
    acc[type] = idx
    return acc
  },
  {} as Record<string, number>,
)

export const SendFaxModal = ({ order, open, onClose }: IProps) => {
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState<string>('')
  const [faxNumber, setFaxNumber] = useState<string>('')
  const [showError, setShowError] = useState<boolean>(false)
  const [sendFax, { isLoading }] = useSendFaxMutation()

  // Group documents by uploadType, matching the same structure as DocumentPanel
  const documentsByType = useMemo(() => {
    if (!order?.files) return {}

    const grouped: Record<string, (typeof order.files)[string][]> = {}
    Object.values(order.files).forEach((file) => {
      const uploadType = file.uploadType
      if (!grouped[uploadType]) {
        grouped[uploadType] = []
      }
      grouped[uploadType].push(file)
    })

    return grouped
  }, [order?.files])

  // Get uploadTypes in the same order as DocumentPanel
  const uploadTypes = useMemo(() => {
    const types = Object.keys(documentsByType).filter((i) =>
      [
        'RX',
        'RX_CANDIDATE',
        'AOB',
        'OFFICE_NOTES',
        'BILLING_DOC',
        'OTHER',
        'SUPPLEMENTAL',
        'REPORT_PDF_ANY',
        'REPORT_PDF',
        'OTHER_BRIGHTREE',
      ].includes(i),
    )
    return types.sort((a, b) => (documentSortOrder[a] ?? 999) - (documentSortOrder[b] ?? 999))
  }, [documentsByType])

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedDocuments(new Set())
      setMessage('')
      setFaxNumber('')
      setShowError(false)
    }
  }, [open])

  const handleDocumentToggle = (fileKey: string) => {
    setSelectedDocuments((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(fileKey)) {
        newSet.delete(fileKey)
      } else {
        newSet.add(fileKey)
      }
      return newSet
    })
  }

  const handleSubmit = async () => {
    const rawNumber = faxNumber?.match(/\d/g)?.join('')

    // Validate fax number
    if (!rawNumber || rawNumber.length !== 10) {
      setShowError(true)
      return
    }

    // Validate at least one document is selected
    if (selectedDocuments.size === 0) {
      setShowError(true)
      return
    }

    // Prepare request body
    const requestBody = {
      orderId: +order.id,
      documentKeys: Array.from(selectedDocuments),
      message: message.trim(),
      faxNumber: `+1${rawNumber}`,
    }

    try {
      await sendFax(requestBody).unwrap()
      toast.success('Fax request queued successfully.')
      onClose()
    } catch (err: any) {
      toast.error(err?.data?.message || 'Could not queue fax request.')
    }
  }

  return (
    <Modal centered show={open} onHide={onClose}>
      <Modal.Header>
        <Modal.Title>Send Fax</Modal.Title>
        <button type="button" className="close" aria-label="Close" onClick={onClose}>
          <span css={{ fontSize: '1.5rem' }} aria-hidden="true">
            &times;
          </span>
        </button>
      </Modal.Header>

      <Modal.Body>
        <ModalBodyContent>
          <FormGroup>
            <Label>
              Select Documents <span className="text-danger">*</span>
            </Label>
            {uploadTypes.length === 0 ? (
              <div css={{ padding: '1rem', textAlign: 'center', color: '#6c757d' }}>
                No documents available in this order
              </div>
            ) : (
              <DocumentsSection>
                {uploadTypes.map((uploadType) => (
                  <DocumentGroup key={uploadType}>
                    {uploadType?.startsWith('REPORT') ? (
                      <GroupHeader>REPORT</GroupHeader>
                    ) : (
                      <GroupHeader>
                        {uploadType?.replace(/_/g, ' ')}
                        {uploadType === 'RX' && order?.officeNotesInRx && ' / Office Notes'}
                      </GroupHeader>
                    )}
                    {documentsByType[uploadType]?.map((file) => {
                      const isSelected = selectedDocuments.has(file.key)
                      return (
                        <DocumentItem
                          key={file.key}
                          selected={isSelected}
                          onClick={() => handleDocumentToggle(file.key)}>
                          <div onClick={(e) => e.stopPropagation()}>
                            <CheckboxComponent
                              value={isSelected}
                              handleChange={(e) => {
                                e.stopPropagation()
                                handleDocumentToggle(file.key)
                              }}
                              label=""
                              parentClassName="custom-control"
                              id={`doc-${file.key}`}
                            />
                          </div>
                          <DocumentInfo>
                            <DocumentName>{file.filename}</DocumentName>
                            <DocumentMeta>
                              Uploaded{' '}
                              {file.uploadDate ? moment(file.uploadDate).format('lll') : ''}
                            </DocumentMeta>
                          </DocumentInfo>
                        </DocumentItem>
                      )
                    })}
                  </DocumentGroup>
                ))}
              </DocumentsSection>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="fax-message">Message</Label>
            <TextArea
              id="fax-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter a message to include with the fax..."
            />
          </FormGroup>

          <FormGroup>
            <Label>
              Fax Number <span className="text-danger">*</span>
            </Label>
            <MaskedInput
              value={faxNumber}
              type="text"
              onChange={(e) => {
                setShowError(false)
                setFaxNumber(e.target.value)
              }}
              mask={phoneNumberMask}
              className="form-control"
              placeholder="(123) 456-7890"
            />
            {showError && (
              <ErrorMessage>
                {selectedDocuments.size === 0
                  ? 'Please select at least one document'
                  : 'The fax number appears to be invalid. Check the number and try again.'}
              </ErrorMessage>
            )}
          </FormGroup>
        </ModalBodyContent>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? 'Submitting...' : 'Submit'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
